import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { demosService } from '../../services/demos';

function formatMonthLabel(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
}

function monthOptions() {
  const now = new Date();
  const opts = [];
  for (let i = 0; i < 6; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    opts.push({ value: val, label: formatMonthLabel(val) });
  }
  return opts;
}

function defaultLastTwoMonths() {
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const previous = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
  return { current, previous };
}

/**
 * Picker for demo-ready student performers (submitted/approved monthly assessment).
 */
export default function DemoReadyStudentPicker({
  selectedIds = [],
  onChange,
  excludeDemoId = null,
  disabled = false,
}) {
  const months = useMemo(() => monthOptions(), []);
  const { current, previous } = useMemo(() => defaultLastTwoMonths(), []);
  const [monthFilter, setMonthFilter] = useState('last2');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [monthRange, setMonthRange] = useState({ monthFrom: previous, monthTo: current });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const queryParams = useMemo(() => {
    const base = {
      search: debouncedSearch,
      page,
      limit: 15,
      exclude_demo_id: excludeDemoId || undefined,
    };
    if (monthFilter === 'last2') {
      return { ...base, month_from: previous, month_to: current };
    }
    return { ...base, month_from: monthFilter, month_to: monthFilter };
  }, [debouncedSearch, page, excludeDemoId, monthFilter, previous, current]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await demosService.getDemoReadyStudents(queryParams);
      const body = res?.data ?? res;
      setRows(body?.data || []);
      setPagination(body?.pagination || { page: 1, total: 0, pages: 0 });
      setMonthRange({ monthFrom: body?.monthFrom, monthTo: body?.monthTo });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load demo-ready students');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, monthFilter, excludeDemoId]);

  const selectedSet = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);

  const toggleStudent = (studentId) => {
    const id = String(studentId);
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  const rangeLabel =
    monthFilter === 'last2'
      ? `${formatMonthLabel(previous)} – ${formatMonthLabel(current)}`
      : formatMonthLabel(monthFilter);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Student performers (demo-ready)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Students marked <strong>Is Child Ready = Yes</strong> on a submitted or approved monthly
        assessment. Showing: {rangeLabel}.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search name or student ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
          <InputLabel>Month</InputLabel>
          <Select
            label="Month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            <MenuItem value="last2">Last 2 months</MenuItem>
            {months.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label} only
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {selectedIds?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
          {selectedIds.map((id) => {
            const row = rows.find((r) => String(r.studentId) === String(id));
            const name = row
              ? `${row.firstName || ''} ${row.lastName || ''}`.trim()
              : id.slice(0, 8);
            return (
              <Chip
                key={id}
                size="small"
                label={name}
                onDelete={
                  disabled
                    ? undefined
                    : () => onChange(selectedIds.filter((x) => String(x) !== String(id)))
                }
              />
            );
          })}
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ maxHeight: 280, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Student</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Teacher</TableCell>
              <TableCell>Month</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No demo-ready students in this period.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const id = String(row.studentId);
                const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
                return (
                  <TableRow key={id} hover selected={selectedSet.has(id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSet.has(id)}
                        disabled={disabled}
                        onChange={() => toggleStudent(id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {name || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.displayId}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.batchName || '—'}</TableCell>
                    <TableCell>{row.teacherName || '—'}</TableCell>
                    <TableCell>
                      {formatMonthLabel(row.reportMonth)}
                      {row.otherReadyMonths?.length > 0 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Also {row.otherReadyMonths.map(formatMonthLabel).join(', ')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.pages > 1 && (
        <Stack alignItems="center" sx={{ mt: 1 }}>
          <Pagination
            size="small"
            page={page}
            count={pagination.pages}
            onChange={(_, p) => setPage(p)}
            disabled={disabled || loading}
          />
        </Stack>
      )}

      {!loading && rows.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {pagination.total} student(s) · page {pagination.page} of {pagination.pages || 1}
          {monthRange.monthFrom && monthRange.monthTo ? ` · ${monthRange.monthFrom} to ${monthRange.monthTo}` : ''}
        </Typography>
      )}
    </Box>
  );
}
